import {
  ComposedChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  Box,
  Card,
  CardContent,
  SxProps,
  Theme,
  useTheme,
  Tooltip as MuiTooltip,
} from "@mui/material";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AnalyticsGraphResponse } from "../../../../../models/Dashboard/Analytics";

export interface AppAreaBarChartData {
  labels: string[];
  values: any[];
}

export function parseAnalyticsGraphDataToAppAreaBarChartData(
  response: AnalyticsGraphResponse[]
): AppAreaBarChartData {
  if (!response || response.length === 0) {
    return { labels: [], values: [] };
  }

  const formattedData: Record<string, any> = {};

  response.forEach(({ label, values }) => {
    values.forEach(({ label: date, value }) => {
      const dayName = new Date(date).toISOString().split("T")[0];

      if (!formattedData[dayName]) {
        formattedData[dayName] = {
          name: dayName,
          inward: 0,
          projects: 0,
          shipments: 0,
        };
      }

      if (label === "Projects") {
        formattedData[dayName].projects = value;
      } else if (label === "Shipments") {
        formattedData[dayName].shipments = value;
      } else if (label === "Items Inward") {
        formattedData[dayName].inward = Number(value);
      }
    });
  });

  return {
    labels: Object.keys(formattedData),
    values: Object.values(formattedData),
  };
}

export interface CombinedChartProps {
  title: string;
  message?: string;
  data: AppAreaBarChartData;
  sx?: SxProps<Theme>;
  barDataKey: string;
  areaDataKeys: { key: string; color: string }[];
  barSize?: number;
  height?: number;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  const theme = useTheme();

  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper + "CC",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1,
          padding: 1,
          boxShadow: theme.shadows[2],
        }}
      >
        <CustomTypography variant="body2" fontWeight={600}>
          {label}
        </CustomTypography>
        {payload.map((entry: any, index: number) => (
          <Box
            key={`tooltip-${index}`}
            sx={{
              display: "flex",
              alignItems: "center",
              marginTop: 0.5,
              color: entry.color,
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                backgroundColor: entry.color,
                marginRight: 1,
              }}
            />
            <CustomTypography variant="body2">
              {entry.name}: {entry.value}
            </CustomTypography>
          </Box>
        ))}
      </Box>
    );
  }

  return null;
};

export default function CombinedChart({
  title,
  message,
  data,
  sx,
  barDataKey,
  areaDataKeys,
  barSize = 20,
  height = 400,
}: CombinedChartProps) {
  const theme = useTheme();

  return (
    <Card>
      <CardContent sx={{ margin: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <CustomTypography
            variant="heading4"
            color={theme.palette.customColors.blackBorder}
            style={{ marginBottom: 24 }}
          >
            {title}
          </CustomTypography>

          {message && (
            <MuiTooltip title={message}>
              <InfoOutlinedIcon
                sx={{ cursor: "pointer", color: theme.palette.grey[600] }}
              />
            </MuiTooltip>
          )}
        </Box>
        {data.values.length > 0 && (
          <Box sx={{ width: "100%", overflowY: "hidden", paddingBottom: 3 }}>
            <Box
              sx={{
                width:
                  data.values.length > 7
                    ? `${data.values.length * 100}px`
                    : "100%",
              }}
            >
              <ResponsiveContainer width="100%" height={height}>
                <ComposedChart
                  data={data.values}
                  margin={{ top: 5, right: 40, left: 20, bottom: 20 }}
                >
                  <XAxis
                    axisLine={false}
                    dataKey="name"
                    fontSize={12}
                    scale="point"
                    padding={{ left: 20, right: 20 }}
                  />
                  <YAxis axisLine={false} fontSize={12} />

                  <CartesianGrid horizontal={true} vertical={false} />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="top"
                    align="left"
                    wrapperStyle={{ paddingBottom: "1rem",fontSize:"0.8rem" }}
                  />
                  <Bar
                    dataKey={barDataKey}
                    barSize={barSize}
                    fill={theme.palette.mintGreen.main}
                    radius={[4, 4, 0, 0]}
                    name="Items Inward"
                  />
                  {areaDataKeys.map(({ key, color }) => (
                    <Area
                      key={key}
                      type="monotone"
                      dataKey={key}
                      fillOpacity={0.2}
                      stroke={color}
                      fill={color}
                      activeDot={{ r: 6 }}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </Box>
        )}
        {data.values.length === 0 && (
          <Box
            sx={{
              width: "100%",
              height: { height },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 3.5,
            }}
          >
            <CustomTypography variant="heading8" color="textSecondary">
              Nothing to show for the selected filters
            </CustomTypography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
